import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Heading,
    Text,
    Tag,
    Flex,
    Icon,
    Progress,
    Spinner,
    Center,
} from '@chakra-ui/react';
import { FiActivity, FiCalendar, FiZap, FiBarChart2 } from 'react-icons/fi';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import axios from 'axios';
import moment from 'moment';

import { PRIMARY_BG } from '../../../theme/brand';
const EventsTracking = ({ date, refresh }) => {
    const [loading, setLoading] = useState(false);
    const [eventData, setEventData] = useState(null);

    const getApiHeaders = () => ({
        "x-api-key": localStorage.getItem("api-key"),
        "x-access-token": localStorage.getItem("customer-portal"),
    });
    const dateformated = moment(date);
    const month = dateformated.format("MM");
    const year = dateformated.format("YYYY");
    const fetchEventTracking = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/dashboard/customer/event-tracking?month=${month}&year=${year}${refresh}`;
            const response = await axios.get(url, { headers: getApiHeaders() });

            if (response.data.success) {
                setEventData(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching event tracking:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEventTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    // Format date for display (e.g., "Dec 2")
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Format month for display (e.g., "Jan")
    const formatMonth = (monthString) => {
        const [year, month] = monthString.split('-');
        const date = new Date(year, month - 1);
        return date.toLocaleDateString('en-US', { month: 'short' });
    };

    // Daily Events Chart Configuration
    const dailyChartOptions = eventData ? {
        chart: {
            type: 'column',
            height: 600,
        },
        title: {
            text: null,
        },
        xAxis: {
            categories: eventData.daily_event_tracking.points.map(d => formatDate(d.date)),
        },
        yAxis: {
            title: {
                text: 'Events',
            },
        },
        tooltip: {
            formatter: function () {
                return `<b>${this.x}</b><br/>Events: ${this.y.toLocaleString()}`;
            }
        },
        series: [
            {
                name: 'Events',
                data: eventData.daily_event_tracking.points.map(d => d.count),
                color: PRIMARY_BG,
            },
        ],
        credits: {
            enabled: false,
        },
        legend: {
            enabled: false,
        },
    } : {};

    // Monthly Events Chart Configuration
    const monthlyChartOptions = eventData ? {
        chart: {
            type: 'line',
            height: 600,
        },
        title: {
            text: null,
        },
        xAxis: {
            categories: eventData.monthly_events_captured.points.map(d => formatMonth(d.month)),
        },
        yAxis: {
            title: {
                text: 'Events',
            },
        },
        tooltip: {
            formatter: function () {
                return `<b>${this.x}</b><br/>Events: ${this.y.toLocaleString()}`;
            }
        },
        series: [
            {
                name: 'Events',
                data: eventData.monthly_events_captured.points.map(d => d.count),
                color: PRIMARY_BG,
            },
        ],
        credits: {
            enabled: false,
        },
        legend: {
            enabled: false,
        },
    } : {};

    if (loading) {
        return (
            <Center h="400px">
                <Spinner size="xl" color={PRIMARY_BG} thickness="4px" />
            </Center>
        );
    }

    if (!eventData) {
        return (
            <Center h="400px">
                <Text color="gray.600">No data available</Text>
            </Center>
        );
    }

    const { summary, top_events_captured } = eventData;

    return (
        <Box p={6}>
            {/* Event Metrics */}
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={6} mb={6}>
                <Box className='box-style-card'>
                    <Flex alignItems="center" mb={2}>
                        <Stat>
                            <Flex alignItems="center" mb={2} justifyContent={"space-between"}>
                                <StatLabel fontSize="sm" color="gray.600">Total Events Tracked</StatLabel>
                                <Icon as={FiActivity} boxSize={5} color="#64748b" mr={2} />
                            </Flex>
                            <StatNumber fontSize="2xl">{summary.total_events_tracked.toLocaleString()}</StatNumber>
                            <StatHelpText mb={0} fontSize={"xs"} color={"#64748b"}>
                                <Flex gap={1}>  {summary?.total_events_change_percent > 0 ? <Text fontSize={"xs"} color={"green"}>{summary?.total_events_change_percent}%</Text> : <Text fontSize={"xs"} color={"red"}>{summary?.total_events_change_percent}%</Text>} from last month</Flex>
                            </StatHelpText>
                        </Stat>
                    </Flex>
                </Box>

                <Box className='box-style-card'>
                    <Box>
                        <Flex alignItems="center" mb={2}>
                            <Stat>
                                <Flex alignItems="center" mb={2} justifyContent={"space-between"}>
                                    <StatLabel fontSize="sm" color="gray.600">Events Today</StatLabel>
                                    <Icon as={FiCalendar} boxSize={5} color="#64748b" mr={2} />
                                </Flex>
                                <StatNumber fontSize="2xl">{summary.events_today.toLocaleString()}</StatNumber>
                                <StatHelpText mb={0} fontSize={"xs"} color={"#64748b"}>
                                    <Flex gap={1}>  {summary?.summary?.events_today_percentage > 0 ? <Text fontSize={"xs"} color={"green"}>{summary?.summary?.events_today_percentage}%</Text> : <Text fontSize={"xs"} color={"red"}>{summary?.summary?.events_today_percentage || 0}%</Text>} vs yesterday</Flex>

                                </StatHelpText>
                            </Stat>
                        </Flex>
                    </Box>
                </Box>

                <Box className='box-style-card'>
                    <Box>
                        <Flex alignItems="center" mb={2}>
                            <Stat>
                                <Flex alignItems="center" mb={2} justifyContent={"space-between"}>
                                    <StatLabel fontSize="sm" color="gray.600">Unique Event Types</StatLabel>
                                    <Icon as={FiZap} boxSize={5} color="#64748b" mr={2} />
                                </Flex>
                                <StatNumber fontSize="2xl">{summary.unique_event_types}</StatNumber>
                                <StatHelpText mb={0} fontSize={"xs"} color={"#64748b"}>Active event types</StatHelpText>
                            </Stat>
                        </Flex>
                    </Box>
                </Box>

                <Box className='box-style-card'>
                    <Box>
                        <Flex alignItems="center" mb={2}>
                            <Stat>
                                <Flex alignItems="center" mb={2} justifyContent={"space-between"}>
                                    <StatLabel fontSize="sm" color="gray.600">Events This Month</StatLabel>
                                    <Icon as={FiBarChart2} boxSize={5} color="#64748b" mr={2} />
                                </Flex>
                                <StatNumber fontSize="2xl">{summary.events_this_month.toLocaleString()}</StatNumber>
                                <StatHelpText mb={0} fontSize={"xs"} color={"#64748b"}>
                                    {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                </StatHelpText>
                            </Stat>
                        </Flex>
                    </Box>
                </Box>
            </Grid>

            {/* Charts */}
            <Grid templateColumns={{ base: '1fr', lg: 'repeat(1, 1fr)' }} gap={6} mb={6}>
                <Box className='box-style-card' p={4}>
                    <Box mb={4}>
                        <Heading size="md">Daily Event Tracking</Heading>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                            Event volume Of {moment(month).format("MMM")}
                        </Text>
                    </Box>
                    <Box>
                        <HighchartsReact highcharts={Highcharts} options={dailyChartOptions} />
                    </Box>
                </Box>

                <Box className='box-style-card' p={4}>
                    <Box mb={4}>
                        <Heading size="md">Monthly Events Captured</Heading>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                            Event volume from {formatMonth(eventData.monthly_events_captured.start_month)} to {formatMonth(eventData.monthly_events_captured.end_month)}
                        </Text>
                    </Box>
                    <Box>
                        <HighchartsReact highcharts={Highcharts} options={monthlyChartOptions} />
                    </Box>
                </Box>
            </Grid>

            {/* Top Events Table */}
            <Box className='box-style-card' p={4}>
                <Box mb={4}>
                    <Heading size="md">Top {top_events_captured.events.length} Events Captured</Heading>
                    <Text fontSize="sm" color="gray.600" mt={1}>
                        Most frequently tracked events
                    </Text>
                </Box>
                <Box>
                    <TableContainer>
                        <Table variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>Event Name</Th>
                                    <Th isNumeric>Count</Th>
                                    <Th isNumeric>Percentage</Th>
                                    <Th>Distribution</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {top_events_captured.events.map((event, index) => (
                                    <Tr key={index}>
                                        <Td>
                                            <Text>{event.event_name}</Text>
                                        </Td>
                                        <Td isNumeric fontWeight="medium">
                                            {event.count.toLocaleString()}
                                        </Td>
                                        <Td isNumeric>
                                            <Tag size={"sm"} bg={"#f2edee"} color={"#1e293b"}>
                                                {event.percentage}%
                                            </Tag>
                                        </Td>
                                        <Td>
                                            <Progress
                                                value={event.distribution}
                                                size="sm"
                                                sx={{
                                                    "& > div": {
                                                        background: PRIMARY_BG,
                                                    },
                                                }}
                                                borderRadius="full"
                                                bg="gray.200"
                                            />
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
        </Box>
    );
};

export default EventsTracking;